import Dexie, { type Table } from 'dexie'

/**
 * Réplique locale (§7.4).
 *
 * ⭐ UNE BASE PAR BOUTIQUE — `iobiz_{storeId}`. Changer de boutique n'écrase
 * jamais la réplique d'une autre, et se déconnecter d'une boutique peut purger
 * sa base sans toucher aux autres. Une base unique avec une colonne `storeId`
 * aurait rendu chaque requête dépendante d'un filtre qu'on finit toujours par
 * oublier quelque part.
 */

/** Champs de synchronisation présents sur chaque ligne répliquée (§7.4). */
export interface SyncFields {
  /** Horodatage serveur : l'horloge de l'appareil ne fait jamais autorité. */
  updated_at?: string
  version: number
  /** Modifiée localement, pas encore confirmée par le serveur. */
  _dirty?: 0 | 1
  /** Le serveur n'a jamais vu cette ligne. */
  _localOnly?: 0 | 1
}

export interface LocalRow extends SyncFields {
  id: string
  [key: string]: unknown
}

/** Une écriture en attente d'envoi (§7.6). */
export interface OutboxEntry {
  /** UUIDv7 — sert AUSSI de clé d'idempotence côté serveur. */
  id: string
  entity: 'customer' | 'item' | 'supplier' | 'transaction' | 'appointment'
  entityId: string
  op: 'create' | 'update' | 'delete'
  payload: Record<string, unknown>
  baseVersion: number | null
  createdAt: string
  attempts: number
  lastError?: string
  status: 'pending' | 'sending' | 'failed' | 'conflict'
  /** Prochaine tentative au plus tôt (reprise exponentielle). */
  nextAttemptAt: number
}

export interface SyncMeta {
  key: string
  value: unknown
}

export interface ConflictRow {
  id: string
  entity: string
  entityId: string
  fields: string[]
  localPayload: Record<string, unknown>
  serverState: Record<string, unknown>
  serverVersion: number
  detectedAt: string
}

export class IobizDB extends Dexie {
  customers!: Table<LocalRow, string>
  items!: Table<LocalRow, string>
  categories!: Table<LocalRow, string>
  suppliers!: Table<LocalRow, string>
  stockLevels!: Table<LocalRow, string>
  transactions!: Table<LocalRow, string>
  taxes!: Table<LocalRow, string>
  appointments!: Table<LocalRow, string>
  cashSessions!: Table<LocalRow, string>
  cashMovements!: Table<LocalRow, string>
  blocages!: Table<LocalRow, string>
  outbox!: Table<OutboxEntry, string>
  syncMeta!: Table<SyncMeta, string>
  conflicts!: Table<ConflictRow, string>

  constructor(storeId: string) {
    super(`iobiz_${storeId}`)

    // Les index déclarés ici sont ceux qui servent aux écrans : trier la liste
    // clients par nom, filtrer par ville, retrouver un article par code-barres.
    // Un index non utilisé coûte à chaque écriture sans rien rapporter.
    this.version(1).stores({
      customers: 'id, last_name, company_name, city, phone, email, _dirty, updated_at',
      items: 'id, name, sku, barcode, category_id, supplier_id, kind, status, _dirty',
      categories: 'id, parent_id, path',
      suppliers: 'id, company_name, city, _dirty',
      stockLevels: 'id, item_id',
      outbox: 'id, status, nextAttemptAt, entityId',
      syncMeta: 'key',
      conflicts: 'id, entity, entityId',
    })

    /**
     * ⚠️ TOUTE table ajoutée l'est dans une NOUVELLE version, jamais en
     * retouchant la version 1.
     *
     * IndexedDB ne crée de magasin qu'au cours d'une montée de version : une
     * table déclarée après coup dans `version(1)` n'existe PAS sur les bases
     * déjà ouvertes. Le défaut ne se voit pas en développement — un navigateur
     * neuf crée tout d'un coup — mais chez l'utilisateur, dont la base a des
     * semaines, la lecture échoue et l'écran qui en dépend disparaît sans
     * message. C'est exactement ce qui a fait s'évanouir le champ TVA du
     * formulaire d'article.
     *
     * Dexie applique les versions dans l'ordre : les bases existantes montent
     * en 2, les neuves sont créées directement complètes.
     */
    this.version(2).stores({
      // Les ventes du jour se relisent hors ligne : on indexe ce qui sert
      // vraiment à l'écran — la date, le statut, la référence.
      transactions: 'id, issue_date, status, doc_type, number, local_ref, customer_id, _dirty',
      taxes: 'id, rate, is_default',
    })

    /**
     * ⚠️ Version 3, et non un ajout dans la 2 : la remarque ci-dessus vaut
     * chaque fois. Une base ouverte depuis des semaines ne connaîtrait pas la
     * table, et l'agenda s'évanouirait sans message chez l'utilisateur — jamais
     * chez nous, dont le navigateur crée tout d'un coup.
     */
    this.version(3).stores({
      // On indexe ce qui sert à l'écran : la borne de début pour découper une
      // journée, le client pour sa fiche, le statut pour écarter les annulés.
      appointments: 'id, starts_at, customer_id, item_id, transaction_id, status, _dirty',
    })

    /**
     * ⚠️ Version 4, même raison. Voir la note de la version 2.
     *
     * ⭐ La caisse descend sur l'appareil parce que le verrou « pas de vente
     * sans caisse ouverte » en dépend. Aller demander au serveur si le tiroir
     * est ouvert ferait échouer le verrou à la première coupure — c'est-à-dire
     * qu'il bloquerait la vente précisément là où le produit promet de tenir.
     */
    this.version(4).stores({
      // `status` indexé : la seule question posée cent fois par jour est
      // « y a-t-il une caisse ouverte ? ».
      cashSessions: 'id, status, opened_at',
      cashMovements: 'id, session_id, created_at',
    })

    /**
     * ⚠️ Version 5 : `session_id` s'ajoute aux index des VENTES.
     *
     * Les totaux d'une session — encaissé en espèces, nombre de ventes — se
     * calculent sur l'appareil, à partir des ventes rattachées au tiroir. Sans
     * cet index, il faudrait parcourir tout l'historique de la boutique à
     * chaque affichage de l'écran de caisse.
     */
    this.version(5).stores({
      transactions:
        'id, issue_date, status, doc_type, number, local_ref, customer_id, session_id, _dirty',
    })

    /**
     * ⚠️ Version 6, même raison que la 2 : une table ajoutée dans une version
     * déjà appliquée n'existe pas sur les bases ouvertes depuis des semaines.
     *
     * ⭐ Les BLOCAGES descendent pour que la grille de l'agenda dessine ses
     * places fermées sans réseau. Les demander au serveur au moment d'afficher
     * aurait rendu l'agenda hors ligne trompeur — il aurait proposé des places
     * que la synchronisation refuse ensuite.
     */
    this.version(6).stores({
      // `category_id` et `debut` indexés : la question posée à chaque journée
      // affichée est « quels blocages touchent cette catégorie ce jour-là ? ».
      blocages: 'id, category_id, debut, fin',
    })
  }
}

/**
 * Bases ouvertes, une par boutique.
 *
 * Dexie ne supporte pas deux instances concurrentes sur le même nom : on
 * mémorise donc l'instance plutôt que d'en rouvrir une à chaque appel.
 */
const bases = new Map<string, IobizDB>()

export function dbFor(storeId: string): IobizDB {
  let db = bases.get(storeId)
  if (!db) {
    db = new IobizDB(storeId)
    bases.set(storeId, db)
  }
  return db
}

export async function closeDb(storeId: string): Promise<void> {
  const db = bases.get(storeId)
  if (!db) return
  db.close()
  bases.delete(storeId)
}

/** Purge complète de la réplique d'une boutique (écran W-33, déconnexion). */
export async function wipeStore(storeId: string): Promise<void> {
  const db = dbFor(storeId)
  await db.delete()
  bases.delete(storeId)
}

// ------------------------------------------------------------------ métadonnées

export async function getMeta<T>(db: IobizDB, key: string, fallback: T): Promise<T> {
  const row = await db.syncMeta.get(key)
  return row ? (row.value as T) : fallback
}

export async function setMeta(db: IobizDB, key: string, value: unknown): Promise<void> {
  await db.syncMeta.put({ key, value })
}

/**
 * Version de la FORME répliquée.
 *
 * ⚠️ À incrémenter dès qu'on change les colonnes projetées par le serveur
 * (`PROJECTIONS` dans pg-sync.repository.ts). Sans ce garde-fou, une réplique
 * déjà amorcée conserve indéfiniment l'ancienne forme : le delta ne renvoie
 * que les lignes MODIFIÉES, et une colonne nouvellement projetée n'arrive
 * jamais sur les lignes qui, elles, n'ont pas bougé. Le symptôme est
 * silencieux — un champ vide, pas une erreur.
 */
// 5 : réparation. Les appareils ayant tourné avec la version précédente ont
// laissé passer les changements `stock_level` sans les écrire, tout en
// avançant leur curseur — leur réplique ne les redemanderait jamais. Le
// changement de forme les oblige à tout relire.
//
// 6 : réparation, même mécanique et même cause profonde. Les lignes IMPORTÉES
// avant le correctif portaient l'identifiant de l'appareil qui avait lancé
// l'import ; le filtre anti-écho les lui refusait donc à jamais. Elles
// existaient en base, restaient invisibles à l'écran, et l'import suivant les
// déclarait « déjà présentes » — un blocage sans issue depuis l'interface.
// Le ré-amorçage lit un instantané COMPLET, sans ce filtre : c'est ce qui
// guérit les répliques déjà abîmées, que le correctif seul ne pouvait pas
// atteindre.
//
// 7 : réparation, TROISIÈME occurrence de la même cause. Le rattrapage des
// codes-barres écrit côté serveur, comme l'import, et partait avec
// l'identifiant de l'appareil : les codes s'inscrivaient en base et
// n'atteignaient jamais l'écran, qui continuait d'afficher des articles sans
// code-barres. Trois fois la même leçon : TOUTE écriture en masse déclenchée
// depuis l'interface mais exécutée par le serveur doit partir SANS
// `X-Device-Id` (`sansAppareil` dans le client HTTP).
//
// 8 : nouvelle COLONNE projetée (`birth_date`). C'est le cas nominal du
// garde-fou, pas une réparation : le delta ne renvoie que les lignes
// MODIFIÉES, et les fiches clients qui n'ont pas bougé n'auraient jamais reçu
// la colonne. Elles afficheraient une date de naissance vide sans qu'aucune
// erreur ne le signale.
//
// 9 : nouvelle ENTITÉ répliquée (`transaction`). Les appareils déjà amorcés
// n'ont jamais demandé les ventes : leur delta ne les leur enverrait pas,
// et l'écran de caisse s'ouvrirait sur un historique vide sans qu'aucune
// erreur ne le signale.
//
// 10 : deux COLONNES ajoutées à la projection des ventes — le nom du caissier
// et celui du client. Elles avaient été ajoutées sans toucher à la forme :
// le delta ne renvoie que les lignes MODIFIÉES, donc les ventes déjà
// répliquées ne les recevaient jamais et les colonnes « Client » et
// « Encaissé par » restaient vides, sans erreur pour l'expliquer.
//
// 11 : entité `tax` répliquée, et le TAUX projeté sur chaque article. Sans
// les deux, la caisse hors ligne calculerait une TVA nulle et remettrait au
// client un ticket faux — sans qu'aucune erreur ne le signale.
//
// 12 : réparation. Les taux de TVA créés avant le correctif portaient
// l'identifiant de l'appareil qui les avait saisis : le filtre anti-écho les
// lui refusait à jamais, et son propre formulaire d'article n'en proposait
// aucun. Le ré-amorçage lit un instantané COMPLET, sans ce filtre — seul
// moyen de guérir une réplique déjà privée de ses taux.
//
// 13 : réparation. Les ventes poussées avant le correctif gardaient leur
// référence provisoire à l'écran alors que le serveur leur avait attribué un
// numéro légal : l'auteur d'une écriture ne recevait pas l'enrichissement que
// le serveur y avait apporté, et le delta ne le lui aurait jamais renvoyé.
//
// 14 : réparation. « Encaissé par » restait vide pour les comptes sans prénom
// ni nom — la projection ne repliait pas sur l'adresse électronique. Les ventes
// déjà répliquées portent la valeur nulle d'alors, et le delta ne renvoie que
// ce qui a changé de version : seul un ré-amorçage complet les corrige.
// 15 : la projection s'élargit. Les catégories emportent désormais leur TYPE
// et leurs POSTES, les rendez-vous le poste réservé. Les lignes déjà
// répliquées n'ont pas bougé de version : le delta ne les renverra jamais, et
// l'agenda resterait sans colonnes sur toutes les répliques existantes — y
// compris celles où les catégories de services sont pourtant bien créées.
// 16 : la projection s'élargit encore. Les articles emportent leur case
// « visible sur le site ». Sans ré-amorçage, toute fiche déjà répliquée
// reviendrait décochée à l'écran alors qu'elle paraît bien en vitrine — et le
// premier enregistrement l'aurait retirée du site sans que personne ne l'ait
// voulu.
export const REPLICA_SHAPE = 16

export const META_SHAPE = 'shape'
export const META_CURSOR = 'cursor'
export const META_BOOTSTRAPPED = 'bootstrapped'
export const META_LAST_SYNC = 'lastSyncAt'
